/**
 * Zustand store for the Strategy Swim Lane board.
 *
 * Owns: container (briefs/creatives), organizeBy (strategy/campaigns/personas/canonical),
 * window, lanes payload, recompute job state.
 *
 * Behavioral rules:
 *   - setOrganizeBy + setWindow trigger re-fetch via the effect in SwimLaneBoard.
 *   - setContainer does NOT trigger a re-fetch — the API returns both shapes.
 *   - fetchLanes is debounced at 200ms so rapid mode-strip clicks don't stack.
 */

import { create } from 'zustand';
import {
  swimLanesApi,
  type SwimLanesResponse,
  type SwimLane as SwimLaneData,
} from '@/services/api';

type Container = 'briefs' | 'creatives';
type OrganizeBy = 'strategy' | 'campaigns' | 'personas' | 'canonical';
type Window = '7d' | '14d' | '30d' | '90d';
type RecomputeStatus = 'idle' | 'queued' | 'running' | 'completed' | 'failed';

interface StrategyState {
  container: Container;
  organizeBy: OrganizeBy;
  window: Window;

  lanes: SwimLaneData[];
  isSeed: boolean;
  computedAt: string | null;

  loading: boolean;
  error: string | null;

  recomputeJobId: string | null;
  recomputeStatus: RecomputeStatus;
  recomputeError: string | null;

  setContainer: (c: Container) => void;
  setOrganizeBy: (mode: OrganizeBy) => void;
  setWindow: (w: Window) => void;

  fetchLanes: (clientUid: string, personaId?: string) => Promise<void>;
  triggerRecompute: (clientUid: string) => Promise<void>;
  pollRecompute: (clientUid: string) => Promise<void>;
}

let _fetchTimer: ReturnType<typeof setTimeout> | null = null;
let _pollInterval: ReturnType<typeof setInterval> | null = null;

export const useStrategyStore = create<StrategyState>((set, get) => ({
  container: 'briefs',
  organizeBy: 'strategy',
  window: '30d',

  lanes: [],
  isSeed: false,
  computedAt: null,

  loading: false,
  error: null,

  recomputeJobId: null,
  recomputeStatus: 'idle',
  recomputeError: null,

  setContainer: (c) => set({ container: c }),
  setOrganizeBy: (mode) => set({ organizeBy: mode }),
  setWindow: (w) => set({ window: w }),

  fetchLanes: async (clientUid: string, personaId?: string) => {
    if (_fetchTimer) clearTimeout(_fetchTimer);
    _fetchTimer = setTimeout(async () => {
      const { organizeBy, container, window } = get();
      set({ loading: true, error: null });
      try {
        const response: SwimLanesResponse = await swimLanesApi.list({
          clientUid,
          organizeBy,
          container,
          window,
          personaId,
        });
        set({
          lanes: response.lanes,
          isSeed: response.is_seed,
          computedAt: response.computed_at,
          loading: false,
          error: null,
        });
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Failed to load swim lanes';
        set({ loading: false, error: msg });
      }
    }, 200);
  },

  triggerRecompute: async (clientUid: string) => {
    set({ recomputeStatus: 'queued', recomputeError: null });
    try {
      const result = await swimLanesApi.recompute(clientUid);
      set({ recomputeJobId: result.job_id, recomputeStatus: 'running' });
      // Begin polling
      void get().pollRecompute(clientUid);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Recompute failed';
      set({ recomputeStatus: 'failed', recomputeError: msg });
    }
  },

  pollRecompute: async (clientUid: string) => {
    if (_pollInterval) clearInterval(_pollInterval);
    const { recomputeJobId } = get();
    if (!recomputeJobId) return;
    _pollInterval = setInterval(async () => {
      const jobId = get().recomputeJobId;
      if (!jobId) {
        if (_pollInterval) {
          clearInterval(_pollInterval);
          _pollInterval = null;
        }
        return;
      }
      try {
        const status = await swimLanesApi.pollRecompute(jobId);
        if (status.status === 'completed') {
          if (_pollInterval) {
            clearInterval(_pollInterval);
            _pollInterval = null;
          }
          set({ recomputeStatus: 'completed' });
          // Refresh lanes once recompute completes
          await get().fetchLanes(clientUid);
        } else if (status.status === 'failed') {
          if (_pollInterval) {
            clearInterval(_pollInterval);
            _pollInterval = null;
          }
          set({
            recomputeStatus: 'failed',
            recomputeError: status.error ?? 'Clustering job failed',
          });
        }
      } catch (err: unknown) {
        if (_pollInterval) {
          clearInterval(_pollInterval);
          _pollInterval = null;
        }
        const msg = err instanceof Error ? err.message : 'Polling failed';
        set({ recomputeStatus: 'failed', recomputeError: msg });
      }
    }, 3000);
  },
}));
